import {
  AppNavigation,
  AppNavigationItem,
  AppSortableNavigationItem,
  CollapsibleNavigationGroup,
  SortableListWithChildren,
  Tooltip,
  useLayoutState,
} from "@etframework/ui.components";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FavoritesNavItem } from "../../components/DueDatesNavigation/FavoritesNavItem";
import { useBoardProvider } from "../../contexts/board/boardContext";
import { useBoardViews } from "../../contexts/views/viewsContext";
import { useUpdateViewOrder } from "../../data/mutations/viewMutations";
import useModalEvent from "../../hooks/useModalEvent";

const DueDatesNavigation = () => {
  const { toggleNavigation } = useLayoutState();
  const { ModalEvents, toggleModal } = useModalEvent();
  const { currentBoardId } = useBoardProvider();
  const { views, favoriteViews = [] } = useBoardViews();
  const pathPostfix = currentBoardId ? `?boardId=${currentBoardId}` : "";

  const sortableListRef = useRef(null);
  const [customViewValues, setCustomViewValues] = useState(views);

  const ids = {
    collapseBtn: "dd_views-navigation_collapse-panel-button",
    createViewbutton: "dd_views-navigation_create-view-button",
    allTasksLink: "dd_views-navigation_all-tasks-link",
    assignedToMeLink: "dd_views-navigation_assigned-to-me-link",
    overdueLink: "dd_views-navigation_overdue-tasks-link",
    completedTaskslink: "dd_views-navigation_completed-tasks-link",
    relatedToMeLink: "dd_views-navigation_related-to-me-link",
    favoriteLink: (index) => `dd_views-navigation_favorite-view-${index}-link`,
    customLink: (index) => `dd_views-navigation_custom-view-${index}-link`,
  };

  const { t, ready } = useTranslation(["Layout"], {
    useSuspense: false,
  });

  const toggleNavigationHandler = () => {
    toggleNavigation();
  };

  const createViewHandler = () => {
    toggleModal(ModalEvents.CreateViewModal);
  };

  const { mutate: updateViewOrder } = useUpdateViewOrder();

  const handleDragEnd = (args, orderedItems) => {
    const viewIds = orderedItems.map((view) => view.Id);

    updateViewOrder(
      { views: viewIds },
      {
        onSuccess: () => {},
        onError: () => {},
      }
    );
  };

  useEffect(() => {
    setCustomViewValues(views);
  }, [views]);

  if (!ready) return null;

  return (
    <AppNavigation>
      <CollapsibleNavigationGroup
        header={t("Navigation.FavoritesTitle")}
        iconElement={
          <Tooltip
            className={"d-inline"}
            dark={true}
            content={() => (
              <p className="p-1 rounded">{t("Navigation.ClosePanel")}</p>
            )}
            position="TopCenter"
            showTipPointer={false}
          >
            <i
              onClick={toggleNavigationHandler}
              id={ids.collapseBtn}
              className="fa-regular fa-arrow-left-to-line cursor-pointer"
            ></i>
          </Tooltip>
        }
      >
        {favoriteViews?.length > 0 ? (
          <div className="w-100">
            {favoriteViews?.map((view, index) => (
              <AppNavigationItem
                id={ids.favoriteLink(index)}
                key={ids.favoriteLink(index)}
                text={<FavoritesNavItem view={view} />}
                href={`/duedates/views/favorites/${view.Id}${pathPostfix}`}
              />
            ))}
          </div>
        ) : (
          <div className="ps-4 et-darkest-gray fs-12 fst-italic">
            {t("Navigation.NoRecords")}
          </div>
        )}
      </CollapsibleNavigationGroup>

      <div className="et-bb-light-gray-1 mx-3 mt-2"></div>

      <CollapsibleNavigationGroup header={t("Navigation.BoardViewsTitle")}>
        <AppNavigationItem
          id={ids.assignedToMeLink}
          text={t("Navigation.AssignedToMe")}
          href={`/duedates/views/assignedtome${pathPostfix}`}
          isActive={location.pathname == "/views/assignedtome"}
        />
        <AppNavigationItem
          id={ids.allTasksLink}
          text={t("Navigation.AllTasks")}
          href={`/duedates/views/alltasks${pathPostfix}`}
        />
        <AppNavigationItem
          id={ids.overdueLink}
          text={t("Navigation.OverdueTasks")}
          href={`/duedates/views/overduetasks${pathPostfix}`}
        />
        <AppNavigationItem
          id={ids.completedTaskslink}
          text={t("Navigation.CompletedTasks")}
          href={`/duedates/views/completedtasks${pathPostfix}`}
        />
        <AppNavigationItem
          id={ids.relatedToMeLink}
          text={t("Navigation.RelatedToMe")}
          href={`/duedates/views/relatedtome${pathPostfix}`}
          isActive={location.pathname == "/views/relatedtome"}
        />
        <AppNavigationItem
          id={ids.relatedToMeLink}
          text={t("Navigation.MyCompletedTasks")}
          href={`/duedates/views/mycompletedtasks${pathPostfix}`}
          isActive={location.pathname == "/views/mycompletedtasks"}
        />
      </CollapsibleNavigationGroup>

      <div className="et-bb-light-gray-1 mx-3 mt-2"></div>

      <CollapsibleNavigationGroup
        header={
          <div
            className="d-flex justify-content-start align-items-center gap-2 et-my-views-nav p-1 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              createViewHandler();
            }}
          >
            {t("Navigation.MyViewsTitle")}
            <Tooltip
              className={"d-inline"}
              content={() => {
                return (
                  <p className="rounded">{t("Navigation.PersonalizedView")}</p>
                );
              }}
              position="TopCenter"
              showTipPointer={false}
              dark={true}
            >
              <i className="fa-regular fa-plus fs-12"></i>
            </Tooltip>
          </div>
        }
      >
        {customViewValues?.length > 0 ? (
          <div className="w-100">
            <SortableListWithChildren
              id="FieldTypeSortableList"
              ref={sortableListRef}
              items={customViewValues?.map((input) => input.Id)}
              setItems={setCustomViewValues}
              onDragEnd={handleDragEnd}
            >
              {customViewValues?.map((view) => (
                <AppSortableNavigationItem
                  id={view.Id}
                  key={view.Id}
                  text={view.Name}
                  href={`/duedates/views/${view.Id}${pathPostfix}`}
                />
              ))}
            </SortableListWithChildren>
          </div>
        ) : (
          <div className="ps-4 et-darkest-gray fs-12 fst-italic">
            {t("Navigation.NoRecords")}
          </div>
        )}
      </CollapsibleNavigationGroup>
    </AppNavigation>
  );
};

export default DueDatesNavigation;
